import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'

export interface SubscriptionPlanParameter {
  parameter: string
  value: string
}

/** Определение параметра плана с бэкенда (для форм). */
export interface SubscriptionPlanParameterDefinition {
  parameter: string
  label: string
  type: 'boolean' | 'integer' | 'string'
  default: string
}

export interface SubscriptionPlan {
  id: number
  name: string
  slug: string
  description?: string
  price: string
  sale_price: string | null
  type: string
  available: boolean
  visible_in_catalog?: boolean
  is_renewable?: boolean
  auto_renewal: boolean
  period: number
  interval: string
  duration: string
  parameters?: SubscriptionPlanParameter[]
  created_at: string
  updated_at: string
}

export interface User {
  id: number
  surname?: string
  first_name?: string
  middle_name?: string
  email: string
  teacher_id?: number | null
  consent_to_mailing?: boolean
  created_at: string
}

export interface UserSubscriptionPayment {
  id: number
  status: string
  method?: 'manual' | 'balance'
}

export interface UserSubscription {
  id: number
  user: User
  plan: SubscriptionPlan
  is_active: boolean
  is_cancelled: boolean
  starts_at: string
  expires_at: string
  cancelled_at: string | null
  remaining_days?: number
  parameters?: SubscriptionPlanParameter[]
  payment?: UserSubscriptionPayment | null
  created_at: string
  updated_at: string
}

interface PlansResponse {
  data?: SubscriptionPlan[]
  success?: boolean
  message?: string
}

export const subscriptionsApi = {
  getPlans: async (): Promise<SubscriptionPlan[]> => {
    const response = await apiClient.get<SubscriptionPlan[] | PlansResponse>(API_ENDPOINTS.SUBSCRIPTIONS.PLANS)
    
    // Проверяем разные форматы ответа
    if (Array.isArray(response.data)) {
      // Если ответ - массив напрямую
      return response.data
    }
    
    if (response.data?.data && Array.isArray(response.data.data)) {
      // Если ответ в формате { data: [...] }
      return response.data.data
    }
    
    if (response.data?.success && response.data?.data && Array.isArray(response.data.data)) {
      // Если ответ в формате { success: true, data: [...] }
      return response.data.data
    }
    
    throw new Error(response.data?.message || 'Ошибка загрузки планов: неверный формат ответа')
  },

  /** Список тарифных планов для админа с фильтрами и пагинацией. */
  getAdminPlans: async (params: {
    search?: string
    type?: string
    available?: boolean
    visible_in_catalog?: boolean
    is_renewable?: boolean
    sort?: string
    order?: 'asc' | 'desc'
    page?: number
    per_page?: number
  } = {}): Promise<{ data: SubscriptionPlan[]; meta: { current_page: number; last_page: number; per_page: number; total: number } }> => {
    const searchParams = new URLSearchParams()
    if (params.search) searchParams.set('search', params.search)
    if (params.type) searchParams.set('type', params.type)
    if (typeof params.available === 'boolean') searchParams.set('available', params.available ? '1' : '0')
    if (typeof params.visible_in_catalog === 'boolean') searchParams.set('visible_in_catalog', params.visible_in_catalog ? '1' : '0')
    if (typeof params.is_renewable === 'boolean') searchParams.set('is_renewable', params.is_renewable ? '1' : '0')
    if (params.sort) searchParams.set('sort', params.sort)
    if (params.order) searchParams.set('order', params.order)
    if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
    if (params.page != null) searchParams.set('page', String(params.page))
    const qs = searchParams.toString()
    const url = qs ? `${API_ENDPOINTS.SUBSCRIPTIONS.PLANS}?${qs}` : API_ENDPOINTS.SUBSCRIPTIONS.PLANS
    const response = await apiClient.get<{ data: SubscriptionPlan[]; meta?: { current_page: number; last_page: number; per_page: number; total: number } }>(url)
    const data = response.data?.data ?? response.data ?? []
    const meta = response.data?.meta ?? {
      current_page: params.page ?? 1,
      last_page: 1,
      per_page: params.per_page ?? (Array.isArray(data) ? data.length : 0),
      total: Array.isArray(data) ? data.length : 0,
    }
    return { data: Array.isArray(data) ? data : [], meta }
  },

  createPlan: async (data: Partial<SubscriptionPlan> & { name: string; price: string; period: number; interval: string }): Promise<SubscriptionPlan> => {
    const response = await apiClient.post<{ data: SubscriptionPlan }>(
      API_ENDPOINTS.SUBSCRIPTIONS.PLANS,
      data
    )
    return response.data.data
  },

  updatePlan: async (id: number, data: Partial<SubscriptionPlan>): Promise<SubscriptionPlan> => {
    const response = await apiClient.put<{ data: SubscriptionPlan }>(
      `${API_ENDPOINTS.SUBSCRIPTIONS.PLANS}/${id}`,
      data
    )
    return response.data.data
  },

  deletePlan: async (id: number): Promise<void> => {
    await apiClient.delete(`${API_ENDPOINTS.SUBSCRIPTIONS.PLANS}/${id}`)
  },

  /** Список параметров плана для форм: ключ, перевод, тип, значение по умолчанию. */
  getPlanParameters: async (): Promise<SubscriptionPlanParameterDefinition[]> => {
    const response = await apiClient.get<{ data: SubscriptionPlanParameterDefinition[] }>(
      `${API_ENDPOINTS.SUBSCRIPTIONS.PLANS}/parameters`
    )
    return response.data?.data ?? []
  },

  /** Текущая активная подписка (GET /subscriptions/current). 404 → null. */
  getCurrentSubscription: async (): Promise<UserSubscription | null> => {
    try {
      interface CurrentSubscriptionResponse {
        data?: UserSubscription
      }
      const response = await apiClient.get<CurrentSubscriptionResponse | UserSubscription>(API_ENDPOINTS.SUBSCRIPTIONS.CURRENT)
      if (response.status === 404) return null
      const data = (response.data as CurrentSubscriptionResponse)?.data ?? response.data
      return data && typeof data === 'object' && 'id' in data ? data as UserSubscription : null
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
      if (err.response?.status === 404) return null
      const msg = err.response?.data?.message || err.message || ''
      if (/not found|нет|not exist/i.test(msg)) return null
      throw error
    }
  },

  /** Текущая подписка: приоритетно GET /current, fallback на список подписок. */
  getUserSubscription: async (): Promise<UserSubscription | null> => {
    const current = await subscriptionsApi.getCurrentSubscription()
    if (current) return current
    try {
      interface UserSubscriptionsResponse {
        data?: UserSubscription[]
      }
      const response = await apiClient.get<UserSubscription[] | UserSubscriptionsResponse>(API_ENDPOINTS.SUBSCRIPTIONS.USER)
      const list = Array.isArray(response.data) ? response.data : (response.data as UserSubscriptionsResponse)?.data
      if (Array.isArray(list) && list.length > 0) {
        const active = list.find((s: UserSubscription) => s.is_active && new Date(s.expires_at) > new Date())
        return (active ?? list[0]) as UserSubscription
      }
      return null
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } }
      if (err.response?.status === 404) return null
      throw error
    }
  },

  /** Оформить подписку (учитель). starts_at — опционально. */
  subscribe: async (planId: number, startsAt?: string): Promise<UserSubscription> => {
    const body: { plan_id: number; starts_at?: string } = { plan_id: planId }
    if (startsAt) body.starts_at = startsAt
    const response = await apiClient.post<{ data?: UserSubscription; message?: string }>(
      API_ENDPOINTS.SUBSCRIPTIONS.USER,
      body
    )
    const data = response.data?.data ?? response.data
    if (!data || typeof data !== 'object' || !('id' in data)) {
      throw new Error(response.data?.message || 'Ошибка оформления подписки')
    }
    return data as UserSubscription
  },

  /** Создать подписку пользователю (только админ). */
  createSubscriptionForUser: async (
    userId: number,
    planId: number,
    startsAt: string,
    expiresAt?: string
  ): Promise<UserSubscription> => {
    const body: { user_id: number; plan_id: number; starts_at: string; expires_at?: string } = {
      user_id: userId,
      plan_id: planId,
      starts_at: startsAt,
    }
    if (expiresAt) body.expires_at = expiresAt
    const response = await apiClient.post<{ data?: UserSubscription; message?: string }>(
      API_ENDPOINTS.SUBSCRIPTIONS.USER,
      body
    )
    const data = response.data?.data ?? response.data
    if (!data || typeof data !== 'object' || !('id' in data)) {
      throw new Error(response.data?.message || 'Ошибка создания подписки')
    }
    return data as UserSubscription
  },

  /** Список подписок (для учителя — свои, без пагинации). */
  getSubscriptionsList: async (): Promise<UserSubscription[]> => {
    const response = await apiClient.get<{ data: UserSubscription[] }>(API_ENDPOINTS.SUBSCRIPTIONS.USER)
    const data = response.data?.data ?? response.data
    return Array.isArray(data) ? data : []
  },

  /** Список подписок для админа с фильтрами и пагинацией. */
  getAdminSubscriptions: async (params: {
    plan_id?: number
    status?: 'active' | 'inactive' | 'pending_payment'
    expires_from?: string
    expires_to?: string
    sort?: string
    order?: 'asc' | 'desc'
    search?: string
    per_page?: number
    page?: number
  } = {}): Promise<{ data: UserSubscription[]; meta: { current_page: number; last_page: number; per_page: number; total: number } }> => {
    const searchParams = new URLSearchParams()
    if (params.plan_id != null) searchParams.set('plan_id', String(params.plan_id))
    if (params.status) searchParams.set('status', params.status)
    if (params.expires_from) searchParams.set('expires_from', params.expires_from)
    if (params.expires_to) searchParams.set('expires_to', params.expires_to)
    if (params.sort) searchParams.set('sort', params.sort)
    if (params.order) searchParams.set('order', params.order)
    if (params.search) searchParams.set('search', params.search)
    if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
    if (params.page != null) searchParams.set('page', String(params.page))
    const qs = searchParams.toString()
    const url = qs ? `${API_ENDPOINTS.SUBSCRIPTIONS.USER}?${qs}` : API_ENDPOINTS.SUBSCRIPTIONS.USER
    const response = await apiClient.get<{ data: UserSubscription[]; meta?: { current_page: number; last_page: number; per_page: number; total: number } }>(url)
    const data = response.data?.data ?? response.data?.data ?? []
    const meta = response.data?.meta ?? { current_page: 1, last_page: 1, per_page: 15, total: Array.isArray(data) ? data.length : 0 }
    return { data: Array.isArray(data) ? data : [], meta }
  },

  activateSubscription: async (subscriptionId: number): Promise<UserSubscription> => {
    const response = await apiClient.post<{ data: UserSubscription }>(
      `${API_ENDPOINTS.SUBSCRIPTIONS.USER}/${subscriptionId}/activate`
    )
    const data = response.data?.data ?? response.data
    if (!data || typeof data !== 'object') throw new Error('Invalid response')
    return data as UserSubscription
  },

  deactivateSubscription: async (subscriptionId: number): Promise<void> => {
    await apiClient.post(`${API_ENDPOINTS.SUBSCRIPTIONS.USER}/${subscriptionId}/deactivate`)
  },

  /** Продление одной подписки на N дней (только админ). */
  extendSubscription: async (subscriptionId: number, days: number): Promise<UserSubscription> => {
    const response = await apiClient.post<{ data: UserSubscription }>(
      `${API_ENDPOINTS.SUBSCRIPTIONS.USER}/${subscriptionId}/extend`,
      { days }
    )
    const data = response.data?.data ?? response.data
    if (!data || typeof data !== 'object') throw new Error('Invalid response')
    return data as UserSubscription
  },

  /** Массовое продление активных подписок (только админ). */
  extendAllSubscriptions: async (days: number): Promise<{ count: number; message: string }> => {
    const response = await apiClient.post<{ count: number; message: string }>(
      API_ENDPOINTS.ADMIN.SUBSCRIPTIONS_EXTEND_ALL,
      { days }
    )
    return { count: response.data?.count ?? 0, message: response.data?.message ?? '' }
  },

  cancelSubscription: async (): Promise<void> => {
    const subscription = await subscriptionsApi.getCurrentSubscription() ?? await subscriptionsApi.getUserSubscription()
    
    if (!subscription) {
      throw new Error('Подписка не найдена')
    }

    // Используем POST метод для деактивации подписки
    interface DeactivateResponse {
      data?: unknown
      message?: string
    }
    const response = await apiClient.post<DeactivateResponse>(
      `${API_ENDPOINTS.SUBSCRIPTIONS.USER}/${subscription.id}/deactivate`
    )
    
    // API возвращает { data: {...} } без поля success
    // Если есть data, значит запрос успешен
    if (!response.data?.data) {
      // Проверяем, есть ли ошибка в ответе
      if (response.data?.message) {
        throw new Error(response.data.message)
      }
      // Если нет data и нет message, но статус успешный - всё ок
      if (response.status >= 200 && response.status < 300) {
        return
      }
      throw new Error('Ошибка отмены подписки')
    }
  },
}
